import { RangeSlider, type RangeSliderValue } from '@trustoo/components/RangeSlider/v2'
import React, { useState } from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

const RANGE_SLIDER_USAGE = `import { RangeSlider } from '@trustoo/components/RangeSlider/v2'

// Single value (one handle), uncontrolled
<RangeSlider defaultValue={40} label="bottom" />

// Controlled single value
<RangeSlider value={price} onChange={(v) => setPrice(v as number)} />

// Range (two handles) — value is a [lower, upper] tuple
<RangeSlider
  value={[20, 80]}
  onChange={(v) => setRange(v as [number, number])}
  label="tooltip"
/>

// Custom bounds + step + label formatting
<RangeSlider
  min={0}
  max={1000}
  step={50}
  defaultValue={[200, 600]}
  label="bottom"
  formatLabel={(v) => \`€\${v}\`}
/>`

const StatefulRangeSlider: React.FC<{
    initial: RangeSliderValue
    label?: 'none' | 'bottom' | 'tooltip'
    min?: number
    max?: number
    step?: number
    disabled?: boolean
    formatLabel?: (value: number) => React.ReactNode
}> = ({ initial, label = 'none', min, max, step, disabled, formatLabel }) => {
    const [value, setValue] = useState<RangeSliderValue>(initial)
    const readout = Array.isArray(value) ? `[${value[0]}, ${value[1]}]` : `${value}`
    return (
        <div className="space-y-spacing-lg">
            <RangeSlider
                value={value}
                onChange={setValue}
                label={label}
                min={min}
                max={max}
                step={step}
                disabled={disabled}
                formatLabel={formatLabel}
            />
            <p className="text-xs font-medium text-text-tertiary">
                value: <InlineCode>{readout}</InlineCode>
            </p>
        </div>
    )
}

export const RangeSliderShowcase: React.FC = () => (
    <Section
        title="RangeSlider (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Slider</span> set
                (ComponentSet <InlineCode>5:26890</InlineCode>). Coded as{' '}
                <InlineCode>RangeSlider</InlineCode> to avoid colliding with the unrelated{' '}
                <InlineCode>shared/ui</InlineCode> <InlineCode>Slider</InlineCode> carousel.
                Supports a single handle (<InlineCode>value: number</InlineCode>) or a two-handle
                range (<InlineCode>value: [number, number]</InlineCode>). Import v2 explicitly via{' '}
                <InlineCode>@trustoo/components/RangeSlider/v2</InlineCode> (net-new — no v1 barrel
                yet).
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <div>
                    <h3 className="text-sm font-semibold text-text-primary">Single value</h3>
                    <p className="mt-spacing-xs text-xs text-text-tertiary">
                        One handle; the fill runs from the start to the handle. Drag, click the
                        rail, or tab onto the handle and use the arrow keys.
                    </p>
                </div>
                <div className="flex flex-col gap-spacing-4xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <StatefulRangeSlider initial={40} label="none" />
                    <StatefulRangeSlider initial={60} label="bottom" />
                </div>
            </div>

            <div className="space-y-spacing-md">
                <div>
                    <h3 className="text-sm font-semibold text-text-primary">Range (two handles)</h3>
                    <p className="mt-spacing-xs text-xs text-text-tertiary">
                        Two handles; the fill runs between them. Each handle clamps at its neighbour
                        so they can't cross.
                    </p>
                </div>
                <div className="flex flex-col gap-spacing-6xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl pt-spacing-7xl">
                    <StatefulRangeSlider initial={[20, 70]} label="tooltip" />
                    <StatefulRangeSlider initial={[30, 80]} label="bottom" />
                </div>
            </div>

            <div className="space-y-spacing-md">
                <div>
                    <h3 className="text-sm font-semibold text-text-primary">
                        Custom bounds, step &amp; formatting
                    </h3>
                    <p className="mt-spacing-xs text-xs text-text-tertiary">
                        <InlineCode>min</InlineCode> / <InlineCode>max</InlineCode> /{' '}
                        <InlineCode>step</InlineCode> with a custom{' '}
                        <InlineCode>formatLabel</InlineCode> (here a euro price in steps of 50).
                    </p>
                </div>
                <div className="rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <StatefulRangeSlider
                        initial={[200, 600]}
                        label="bottom"
                        min={0}
                        max={1000}
                        step={50}
                        formatLabel={(v) => `€${v}`}
                    />
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Disabled</h3>
                <div className="flex flex-col gap-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <RangeSlider defaultValue={40} disabled />
                    <RangeSlider defaultValue={[25, 75]} disabled />
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={RANGE_SLIDER_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>value</InlineCode> / <InlineCode>defaultValue</InlineCode> +{' '}
                    <InlineCode>onChange</InlineCode> (a <InlineCode>number</InlineCode> for a
                    single handle, a <InlineCode>[number, number]</InlineCode> tuple for a range),{' '}
                    <InlineCode>onChangeEnd</InlineCode>, <InlineCode>min</InlineCode> /{' '}
                    <InlineCode>max</InlineCode> / <InlineCode>step</InlineCode>,{' '}
                    <InlineCode>label</InlineCode> (none · bottom · tooltip),{' '}
                    <InlineCode>formatLabel</InlineCode>, and <InlineCode>disabled</InlineCode>.
                    Each handle is a <InlineCode>role=&quot;slider&quot;</InlineCode> element with
                    full keyboard support (arrows, <InlineCode>PageUp</InlineCode>/
                    <InlineCode>PageDown</InlineCode>, <InlineCode>Home</InlineCode>/
                    <InlineCode>End</InlineCode>).
                </p>
            </div>
        </div>
    </Section>
)
